/**
 * /w/:slug/app/responses/:id — write and submit a management response
 * (V98-5-7; findings round 3 "management-response authoring: a textarea and
 * a submit"). The response's owner writes (cause · response · what changes ·
 * by when), saves drafts, and submits it for the program approver's
 * sign-off; a send-back comes back here to revise. Marked UNAUDITED:
 * management's words, not the auditor's.
 *
 * Reads: GET /management-responses/:id (any member). Writes: the owner only.
 */
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Field, Input, PageHeader, Spinner, Textarea } from '@aigovops/design-system';
import type { ManagementResponseDraft } from '@aigovops/core';
import { useWorkspace } from '../lib/workspace';
import { getManagementResponse, resubmitManagementResponse, saveManagementResponse, submitManagementResponse } from '../lib/cert-ops-api';
import { fmtDate } from '../lib/cert-ops-model';
import { PROGRAM_LABELS, isProgramKey } from '../lib/nav-config';
import { getToday } from '../lib/api';
import { queryKeys } from '../lib/query-keys';
import { firstName, nameOf } from '../lib/today-model';
import { AskAdmin } from '../components/AskAdmin';
import { Chip, errorText, useAnnounce } from '../components/today/shared';
import { useNames } from './Today';
import './today.css';
import './cert-ops.css';

const EMPTY: ManagementResponseDraft = { cause: '', response: '', remediation: '', remediation_date: null };

export function ResponsePage() {
  const { id = '' } = useParams<{ id: string }>();
  const [sp] = useSearchParams();
  const { workspace } = useWorkspace();
  const slug = workspace.slug, prefix = `/w/${slug}/app`;
  const qc = useQueryClient();
  const names = useNames(slug);
  const today = useQuery({ queryKey: queryKeys.today(slug), queryFn: () => getToday(slug), staleTime: 60_000 });
  const q = useQuery({ queryKey: ['management-response', slug, id], queryFn: () => getManagementResponse(slug, id), retry: false });
  const [d, setD] = useState<ManagementResponseDraft>(EMPTY);
  const [errs, setErrs] = useState<Partial<Record<keyof ManagementResponseDraft, string>>>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [announceNode, announce] = useAnnounce();
  const r = q.data?.response;
  useEffect(() => { if (r?.draft) setD({ ...EMPTY, ...r.draft }); }, [r?.work_item.work_item_id, r?.state]);

  const from = sp.get('from') ?? '';
  const program = from.startsWith('mock:') ? from.slice(5) : null;
  // V98-5 review: a response started from an access review returns to it.
  const review = from.startsWith('review:') ? from.slice('review:'.length) : null;
  const crumb = program && isProgramKey(program)
    ? { to: `${prefix}/programs/${program}/operate/mock-audit`, label: `${PROGRAM_LABELS[program] ?? 'Program'} · Mock audit` }
    : review ? { to: `${prefix}/items/access_review/${encodeURIComponent(review)}?from=today`, label: 'Access review' }
      : { to: `${prefix}/today`, label: 'Today' };
  const me = today.data?.viewer.user_id ?? '';
  const author = !!r && r.work_item.owner_user_ids.includes(me);
  const returnedAuthor = !!r && r.state === 'returned' && r.request?.preparer_user_id === me;
  const editable = !!r && ((r.state === 'not_started' || r.state === 'drafting') ? author : returnedAuthor);
  const set = (k: keyof ManagementResponseDraft, v: string) => { setD((x) => ({ ...x, [k]: v })); setErrs((x) => ({ ...x, [k]: undefined })); setErr(null); };
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['management-response', slug, id] }), qc.invalidateQueries({ queryKey: ['mock-audit', slug] }), qc.invalidateQueries({ queryKey: ['today', slug] })]);
  const check = () => {
    const e: typeof errs = {};
    if (d.cause.trim().length < 20) e.cause = 'Say what caused it (at least 20 characters).';
    if (d.response.trim().length < 20) e.response = 'Give management’s response (at least 20 characters).';
    if (d.remediation.trim().length < 10) e.remediation = 'Say what changes so it doesn’t recur (at least 10 characters).';
    setErrs(e);
    return Object.keys(e).length === 0;
  };
  const save = async () => {
    setBusy(true); setErr(null);
    try { await saveManagementResponse(slug, id, d); await refresh(); announce('Draft saved'); }
    catch (e) { setErr(errorText(e, 'The draft could not be saved.')); }
    finally { setBusy(false); }
  };
  const submit = async () => {
    if (!check()) return;
    setBusy(true); setErr(null);
    try {
      if (r?.state === 'returned') await resubmitManagementResponse(slug, id, d); else await submitManagementResponse(slug, id, d);
      await refresh();
      announce('Submitted for sign-off');
      document.getElementById('resp-state')?.focus();
    } catch (e) { setErr(errorText(e, 'The response could not be submitted.')); }
    finally { setBusy(false); }
  };

  return (
    <div className="gp-item">
      {announceNode}
      <nav className="gp-crumbs" aria-label="Breadcrumb">
        <Link to={crumb.to}>{`‹ ${crumb.label}`}</Link><span aria-hidden="true">/</span><span aria-current="page">Management response</span>
      </nav>
      <PageHeader variant="app" title={r?.work_item.title ?? 'Management response'} headingId="resp-h1"
        subtitle={<span className="gp-ow-meta"><Chip text="Unaudited · management’s response" tone="neutral" />{r?.state === 'returned' ? <Chip text="Sent back" tone="red" /> : null}{r?.work_item.due_at ? <span>{`Due ${fmtDate(r.work_item.due_at)}`}</span> : null}</span>} />
      {q.isLoading ? <Spinner label="Loading the response" />
        : q.error || !r ? <Alert variant="error"><p>The response could not load.</p><Button onClick={() => void q.refetch()}>Retry</Button></Alert>
          : (
            <>
              <div className={`gp-state${r.state === 'returned' ? ' gp-state--warn' : r.state === 'signed' ? ' gp-state--ok' : ''}`} id="resp-state" tabIndex={-1} data-part="resp-state">
                <strong>{r.state === 'not_started' ? 'Not started' : r.state === 'drafting' ? 'Draft · not submitted'
                  : r.state === 'submitted' ? `Submitted · with ${r.request?.approver_user_id ? firstName(nameOf(names, r.request.approver_user_id)) : 'the approver'} for sign-off`
                    : r.state === 'returned' ? `Sent back by ${firstName(nameOf(names, r.request?.approver_user_id))}` : `Signed by ${nameOf(names, r.request?.signed_by_user_id)}`}</strong>
                {r.state === 'returned' && r.request?.reason ? <span>{r.request.reason}</span> : null}
                {!editable && (r.state === 'not_started' || r.state === 'drafting') ? <span className="gp-dim">{`${nameOf(names, r.work_item.owner_user_ids[0], 'Its owner')} writes it.`}</span> : null}
                {!editable && r.state === 'returned' ? (
                  <span className="gp-ow-meta"><span>{`${nameOf(names, r.request?.preparer_user_id, 'Its author')} revises it.`}</span>
                    <AskAdmin slug={slug} compact verb="reassign it" what="reassigns responses" subject={`GAC Pro: the management response “${r.work_item.title}” was sent back`} /></span>
                ) : null}
              </div>
              {err ? <Alert variant="error">{err}</Alert> : null}
              {editable ? (
                <form className="gp-form" noValidate aria-label="Management response" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
                  <Field id="resp-cause" label="What caused it" required error={errs.cause}>
                    <Textarea rows={3} maxLength={1200} value={d.cause} onChange={(e) => set('cause', e.currentTarget.value)} />
                  </Field>
                  <Field id="resp-response" label="Management’s response" required error={errs.response}>
                    <Textarea rows={4} maxLength={1200} value={d.response} onChange={(e) => set('response', e.currentTarget.value)} />
                  </Field>
                  <Field id="resp-remediation" label="What changes so it doesn’t recur" required error={errs.remediation}>
                    <Textarea rows={3} maxLength={1200} value={d.remediation} onChange={(e) => set('remediation', e.currentTarget.value)} />
                  </Field>
                  <Field id="resp-date" label="In place by" hint="Optional">
                    <Input type="date" value={d.remediation_date ?? ''} onChange={(e) => set('remediation_date', e.currentTarget.value)} />
                  </Field>
                  <div className="gp-actions">
                    <Button type="submit" variant="primary" aria-disabled={busy || undefined}>{r.state === 'returned' ? 'Resubmit for sign-off' : 'Submit for sign-off'}</Button>
                    {r.state !== 'returned' ? <Button variant="ghost" aria-disabled={busy || undefined} onClick={() => void save()}>Save draft</Button> : null}
                  </div>
                </form>
              ) : r.draft ? (
                <dl className="gp-dl" data-part="resp-read">
                  <dt>What caused it</dt><dd>{r.draft.cause || '—'}</dd>
                  <dt>Response</dt><dd>{r.draft.response || '—'}</dd>
                  <dt>What changes</dt><dd>{r.draft.remediation || '—'}</dd>
                  {r.draft.remediation_date ? <><dt>In place by</dt><dd>{fmtDate(r.draft.remediation_date)}</dd></> : null}
                </dl>
              ) : null}
              {r.request ? <p><Link to={`${prefix}/items/signature_request/${encodeURIComponent(r.request.request_id)}?from=today`}>{`Open the sign-off (v${r.request.version})`}</Link></p> : null}
            </>
          )}
    </div>
  );
}
